import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import api from '../services/api';
import './Scanner.css';

const Scanner = () => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  
  const [scannerInventory, setScannerInventory] = useState([]);
  const [activeScan, setActiveScan] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [nearbyUsers, setNearbyUsers] = useState([]);
  const [discoveredIPs, setDiscoveredIPs] = useState([]);
  const [selectedIP, setSelectedIP] = useState('');
  const [bankAccountData, setBankAccountData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanPhase, setScanPhase] = useState('network'); // 'network' or 'target'
  const [lastCompletedScanId, setLastCompletedScanId] = useState(null);

  // Load scanner data on component mount
  useEffect(() => {
    if (user?.token) {
      fetchScannerData();
      fetchNearbyUsers();
    }
  }, [user?.token]);

  // Initialize lastCompletedScanId when scan history loads
  useEffect(() => {
    if (scanHistory.length > 0 && !lastCompletedScanId) {
      const latestCompletedScan = scanHistory
        .filter(scan => scan.status === 'completed' && scan.type === 'brute-force')
        .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))[0];
      
      if (latestCompletedScan) {
        setLastCompletedScanId(latestCompletedScan._id);
      }
    }
  }, [scanHistory, lastCompletedScanId]);

  // Force radar animations by injecting CSS directly into document head  
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .radar-sweep {
        animation: radarSweep 3s linear infinite !important;
        -webkit-animation: radarSweep 3s linear infinite !important;
        animation-play-state: running !important;
        -webkit-animation-play-state: running !important;
      }
      .target-pulse {
        animation: targetPulse 2s ease-in-out infinite !important;
        -webkit-animation: targetPulse 2s ease-in-out infinite !important;
        animation-play-state: running !important;
        -webkit-animation-play-state: running !important;
      }
      @keyframes radarSweep {
        0% { transform: translate(-50%, -100%) rotate(0deg); opacity: 1; }
        100% { transform: translate(-50%, -100%) rotate(360deg); opacity: 1; }
      }
      @keyframes targetPulse {
        0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
        50% { transform: translate(-50%, -50%) scale(1.5); opacity: 0.7; }
        100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      // Cleanup: remove the style when component unmounts
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, [user?.token]);

  // Auto-refresh scanner status every 5 seconds when there's an active scan
  useEffect(() => {
    let interval;
    if (activeScan && activeScan.status === 'running') {
      interval = setInterval(() => {
        fetchScannerData();
      }, 5000); // Refresh every 5 seconds
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [activeScan?.status]);

  const fetchScannerData = async () => {
    try {
      const response = await api.get('/scanner/status');
      const data = response.data;
        
        // Check for newly completed brute force scans and show notification
        const newCompletedScans = (data.scanHistory || []).filter(scan => 
          (scan.status === 'completed' || scan.status === 'failed') && 
          scan.type === 'brute-force' && 
          scan._id !== lastCompletedScanId &&
          scan.results
        );
        
        if (newCompletedScans.length > 0) {
          const latestScan = newCompletedScans[0]; // Get most recent completed scan
          const stolenAmount = latestScan.results.attackerAmount || 0;
          const victimUsername = latestScan.results.victimUsername || 'Unknown';
          const accountNumber = latestScan.results.accountNumber || 'N/A';
          
          if (latestScan.status === 'failed') {
            // SWAT detected attack notification
            addNotification({
              type: 'attack_detected',
              category: 'attack',
              title: '🚨 Brute Force Attack Detected by SWAT!',
              message: `SWAT detected your attack on ${victimUsername}`,
              details: `Attack on account ${accountNumber} was intercepted by law enforcement`,
              icon: '🚨',
              color: '#FF5722'
            });
          } else if (stolenAmount > 0) {
            // Successful attack notification
            addNotification({
              type: 'attack_completed',
              category: 'attack',
              title: '💰 Brute Force Attack Successful!',
              message: `Successfully breached ${victimUsername}'s savings account`,
              details: `Retrieved: ${stolenAmount} WR Coins from account ${accountNumber}`,
              icon: '💰',
              color: '#4CAF50'
            });
          } else {
            // Failed attack notification (no funds)
            addNotification({
              type: 'attack_failed',
              category: 'attack',
              title: '❌ Brute Force Attack Failed',
              message: `Attack on ${victimUsername} completed but no funds retrieved`,
              details: `Target account ${accountNumber} had insufficient funds`,
              icon: '❌',
              color: '#F44336'
            });
          }
          
          setLastCompletedScanId(latestScan._id);
        }
        
      setScannerInventory(data.scannerInventory || []);
      setActiveScan(data.activeScan);
      setScanHistory(data.scanHistory || []);
      setDiscoveredIPs(data.discoveredIPs || []);
      setBankAccountData(data.bankAccountData);
    } catch (error) {
      console.error('Error fetching scanner data:', error);
    }
  };

  const fetchNearbyUsers = async () => {
    try {
      const response = await api.get('/scanner/nearby-users');
      const data = response.data;
      console.log('Scanner: Nearby users response:', data);
      setNearbyUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching nearby users:', error);
    }
  };

  const startNetworkScan = async () => {
    if (nearbyUsers.length === 0) {
      addNotification({
        title: '❌ No Targets',
        description: 'No users detected within sniffer radius. Activate your sniffer tool first.',
        type: 'error'
      });
      return;
    }

    if (activeScan) {
      addNotification({
        title: '⚠️ Scanner Busy',
        description: 'A scan is already in progress. Please wait for it to complete.',
        type: 'warning'
      });
      return;
    }

    setLoading(true);
    
    try {
      const response = await api.post('/scanner/start-network-scan');
      const data = response.data;
      setActiveScan(data.scan);
      addNotification({
        title: '🔍 Network Scan Started',
        description: `Scanning ${nearbyUsers.length} nearby users for IP addresses`,
        type: 'success'
      });
      
      // Refresh data
      fetchScannerData();
    } catch (error) {
      console.error('Error starting network scan:', error);
      const errorMessage = error.response?.data?.message || 'Failed to start network scan';
      addNotification({
        title: '❌ Scan Failed',
        description: errorMessage,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const startTargetScan = async () => {
    if (!selectedIP) {
      addNotification({
        title: '❌ No Target Selected',
        description: 'Please select an IP address to scan',
        type: 'error'
      });
      return;
    }

    if (activeScan) {
      addNotification({
        title: '⚠️ Scanner Busy',
        description: 'A scan is already in progress. Please wait for it to complete.',
        type: 'warning'
      });
      return;
    }

    setLoading(true);
    
    try {
      const response = await api.post('/scanner/start-target-scan', { 
        targetIP: selectedIP 
      });
      const data = response.data;
      setActiveScan(data.scan);
      addNotification({
        title: '🔍 Target Scan Started',
        description: `Deep scanning ${selectedIP} for bank account data`,
        type: 'success'
      });
      
      // Clear IP selection and refresh data
      setSelectedIP('');
      fetchScannerData();
    } catch (error) {
      console.error('Error starting target scan:', error);
      const errorMessage = error.response?.data?.message || 'Failed to start target scan';
      addNotification({
        title: '❌ Scan Failed',
        description: errorMessage,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const bruteForceBankAccount = async () => {
    if (!bankAccountData) {
      addNotification({
        title: '❌ No Bank Data',
        description: 'No bank account data available to attack',
        type: 'error'
      });
      return;
    }

    if (activeScan) {
      addNotification({
        title: '⚠️ Scanner Busy',
        description: 'A scan is already in progress. Please wait for it to complete.',
        type: 'warning'
      });
      return;
    }

    setLoading(true);
    
    try {
      const response = await api.post('/scanner/start-brute-force', { 
        targetUserId: bankAccountData.userId,
        accountNumber: bankAccountData.accountNumber
      });
      const data = response.data;
      setActiveScan(data.scan);
      addNotification({
        title: '💥 Brute Force Attack Started!',
        description: `Attacking ${bankAccountData.username}'s bank account (13h scan, 70% SWAT risk)`,
        type: 'success'
      });
      
      // Refresh data to show active brute force scan
      fetchScannerData();
    } catch (error) {
      console.error('Error starting brute force attack:', error);
      let errorMessage = error.response?.data?.message || 'Failed to start brute force attack';
      
      // Provide helpful guidance based on the error
      if (errorMessage.includes('complete target analysis first')) {
        errorMessage = 'You must complete target analysis first. Start a Network Discovery scan to find targets, then run Target Analysis.';
      } else if (errorMessage.includes('already have an active scan')) {
        errorMessage = 'You already have an active scan running. Wait for it to complete or cancel it first.';
      } else if (errorMessage.includes('Target user ID mismatch')) {
        errorMessage = 'Target data is outdated. Please run Target Analysis again for this user.';
      }
      
      addNotification({
        title: '❌ Brute Force Failed',
        description: errorMessage,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const cancelScan = async () => {
    if (!activeScan) return;

    setLoading(true);
    
    try {
      await api.post('/scanner/cancel');
      setActiveScan(null);
      addNotification({
        title: '🛑 Scan Cancelled',
        description: 'Active scan has been terminated',
        type: 'info'
      });
      fetchScannerData();
    } catch (error) {
      console.error('Error cancelling scan:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateScanDuration = () => {
    const baseHours = 3; // 3 hours base
    const reductionPerPart = 0.25; // 15 minutes (0.25 hours) per part
    const totalReduction = Math.min(scannerInventory.length * reductionPerPart, 2.5); // Max 2.5 hours reduction
    return Math.max(baseHours - totalReduction, 0.5); // Minimum 30 minutes
  };

  const formatScanTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const clearScanHistory = async () => {
    if (scanHistory.length === 0) {
      addNotification({
        title: '📋 No History',
        description: 'No scan history to clear',
        type: 'info'
      });
      return;
    }

    setLoading(true);
    
    try {
      await api.post('/scanner/clear-history');
      setScanHistory([]);
      addNotification({
        title: '🗑️ History Cleared',
        description: 'Scan history has been deleted successfully',
        type: 'success'
      });
      fetchScannerData(); // Refresh data to confirm
    } catch (error) {
      console.error('Error clearing scan history:', error);
      const errorMessage = error.response?.data?.message || 'Failed to clear scan history';
      addNotification({
        title: '❌ Clear Failed',
        description: errorMessage,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const clearDiscoveredIPs = async () => {
    if (discoveredIPs.length === 0) {
      addNotification({
        title: '📋 No IPs',
        description: 'No discovered IP addresses to clear',
        type: 'info'
      });
      return;
    }

    setLoading(true);
    
    try {
      await api.post('/scanner/clear-ips');
      setDiscoveredIPs([]);
      setBankAccountData(null); // Clear bank data since it's based on IPs
      setSelectedIP(''); // Clear selected IP
      addNotification({
        title: '🗑️ IPs Cleared',
        description: 'Discovered IP addresses have been deleted successfully',
        type: 'success'
      });
      fetchScannerData(); // Refresh data to confirm
    } catch (error) {
      console.error('Error clearing discovered IPs:', error);
      const errorMessage = error.response?.data?.message || 'Failed to clear discovered IPs';
      addNotification({
        title: '❌ Clear Failed',
        description: errorMessage,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getScanDuration = () => {
    if (!activeScan) return null;
    
    const start = new Date(activeScan.startedAt);
    const now = new Date();
    const duration = Math.floor((now - start) / 1000);
    
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="scanner-container">
      <div className="scanner-header">
        <h3>🔍 Advanced Network Scanner</h3>
        <p>Scan computers within sniffer radius and extract bank account data from unprotected users</p>
      </div>

      {/* Scanner Status */}
      <div className="scanner-section">
        <h4>Scanner Status</h4>
        <div className="scanner-status">
          {activeScan ? (
            <div className="active-scan">
              <div className="status-indicator scanning">🔍 SCANNING</div>
              <div className="scan-details">
                <p><strong>Scan Type:</strong> {
                  activeScan.scanType === 'network' ? 'Network Discovery' : 
                  activeScan.scanType === 'target' ? 'Target Analysis' : 
                  'Brute Force Attack'
                }</p>
                <p><strong>Target:</strong> {activeScan.target || 'Nearby Network'}</p>
                <p><strong>Duration:</strong> {getScanDuration()}</p>
                
                {/* Progress Bar */}
                <div className="progress-section">
                  <div className="progress-label">
                    <span><strong>Progress:</strong> {activeScan.progress || 0}%</span>
                    {activeScan.timeRemaining && (
                      <span className="time-remaining">
                        {Math.floor(activeScan.timeRemaining / (1000 * 60 * 60))}h {Math.floor((activeScan.timeRemaining % (1000 * 60 * 60)) / (1000 * 60))}m remaining
                      </span>
                    )}
                  </div>
                  <div className="progress-bar-container">
                    <div 
                      className="progress-bar-fill" 
                      style={{ width: `${activeScan.progress || 0}%` }}
                    ></div>
                  </div>
                </div>
                
                <p><strong>Status:</strong> {activeScan.status}</p>
              </div>
              <button 
                className="btn-cancel"
                onClick={cancelScan}
                disabled={loading}
              >
                🛑 Cancel Scan
              </button>
            </div>
          ) : (
            <div className="idle-status">
              <div className="status-indicator idle">✅ READY</div>
              <p>Scanner is ready for new operation</p>
              <p><strong>Scan Duration:</strong> {calculateScanDuration()} hours (with {scannerInventory.length} scanner parts)</p>
            </div>
          )}
        </div>
      </div>

      {/* Professional Network Discovery */}
      <div className="scanner-section network-discovery-pro">
        <div className="discovery-header-pro">
          <div className="discovery-title">
            <h4><span className="discovery-icon">🌐</span>Network Discovery</h4>
            <div className="discovery-subtitle">Advanced target reconnaissance within sniffer radius</div>
          </div>
          <div className="discovery-controls">
            <div className="targets-counter">
              <span className="counter-value">{nearbyUsers.length}</span>
              <span className="counter-label">TARGETS</span>
            </div>
            <button
              className="refresh-btn-pro"
              onClick={fetchNearbyUsers}
              disabled={loading}
              title="Refresh network scan"
            >
              <span className="refresh-icon">⟳</span>
              <span className="refresh-text">REFRESH</span>
            </button>
          </div>
        </div>

        {nearbyUsers.length > 0 ? (
          <div className="discovery-content-pro">
            {/* Network Status Bar */}
            <div className="network-status-bar">
              <div className="status-item">
                <div className="status-icon online">●</div>
                <span>NETWORK ACTIVE</span>
              </div>
              <div className="status-item">
                <div className="status-icon scanning">📡</div>
                <span>RADAR OPERATIONAL</span>
              </div>
              <div className="status-item">
                <div className="status-icon targets">🎯</div>
                <span>{nearbyUsers.length} CONTACT{nearbyUsers.length !== 1 ? 'S' : ''}</span>
              </div>
            </div>

            {/* Professional Target Grid */}
            <div className="targets-grid-pro">
              {nearbyUsers.map((user, index) => {
                const signalStrength = Math.max(20, 100 - (user.distance / 2));
                const threatLevel = user.distance < 50 ? 'high' : user.distance < 100 ? 'medium' : 'low';
                
                return (
                  <div key={index} className={`target-card-pro ${threatLevel}-threat`}>
                    <div className="target-header">
                      <div className="target-avatar">
                        <div className="avatar-ring"></div>
                        <span className="avatar-icon">👤</span>
                      </div>
                      <div className="target-info">
                        <div className="target-username">{user.username}</div>
                        <div className="target-distance">{user.distance}m away</div>
                      </div>
                      <div className="signal-indicator">
                        <div className="signal-bars">
                          <div className={`bar ${signalStrength > 25 ? 'active' : ''}`}></div>
                          <div className={`bar ${signalStrength > 50 ? 'active' : ''}`}></div>
                          <div className={`bar ${signalStrength > 75 ? 'active' : ''}`}></div>
                          <div className={`bar ${signalStrength > 90 ? 'active' : ''}`}></div>
                        </div>
                        <span className="signal-percent">{Math.round(signalStrength)}%</span>
                      </div>
                    </div>
                    
                    <div className="target-stats">
                      <div className="stat">
                        <span className="stat-label">STATUS</span>
                        <span className="stat-value online">ONLINE</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">SYSTEM</span>
                        <span className="stat-value">VULNERABLE</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">THREAT</span>
                        <span className={`stat-value ${threatLevel}`}>{threatLevel.toUpperCase()}</span>
                      </div>
                    </div>
                    
                    <div className="target-actions">
                      <div className="scan-estimate">
                        <span className="estimate-icon">⏱️</span>
                        <span>Est. {calculateScanDuration()}h scan</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Scan Action Panel */}
            <div className="scan-action-panel-pro">
              <div className="scan-info">
                <div className="scan-details">
                  <h5>Network Penetration Scan</h5>
                  <p>Deep scan {nearbyUsers.length} target{nearbyUsers.length !== 1 ? 's' : ''} for IP addresses and vulnerabilities</p>
                </div>
                <div className="scan-specs">
                  <div className="spec">
                    <span className="spec-label">Duration:</span>
                    <span className="spec-value">{calculateScanDuration()}h</span>
                  </div>
                  <div className="spec">
                    <span className="spec-label">Success Rate:</span>
                    <span className="spec-value">95%</span>
                  </div>
                  <div className="spec">
                    <span className="spec-label">Detection Risk:</span>
                    <span className="spec-value low">Low</span>
                  </div>
                </div>
              </div>
              <button
                className="scan-button-pro"
                onClick={startNetworkScan}
                disabled={activeScan || loading}
              >
                <div className="button-content">
                  <span className="button-icon">🔍</span>
                  <div className="button-text">
                    <span className="button-primary">INITIATE SCAN</span>
                    <span className="button-secondary">{nearbyUsers.length} target{nearbyUsers.length !== 1 ? 's' : ''} • {calculateScanDuration()}h</span>
                  </div>
                </div>
              </button>
            </div>
          </div>
        ) : (
          <div className="no-targets-pro">
            <div className="empty-state">
              <div className="empty-icon">📡</div>
              <h5>No Network Targets Detected</h5>
              <p>Your sniffer radius is clear of vulnerable systems</p>
            </div>
            
            <div className="requirements-panel">
              <h6>Network Discovery Requirements</h6>
              <div className="requirements-grid">
                <div className="requirement">
                  <span className="req-icon">💻</span>
                  <div className="req-content">
                    <span className="req-title">Computer Placement</span>
                    <span className="req-desc">Deploy your system in "Hack The Planet"</span>
                  </div>
                </div>
                <div className="requirement">
                  <span className="req-icon">🎯</span>
                  <div className="req-content">
                    <span className="req-title">Active Targets</span>
                    <span className="req-desc">Users must have computers online</span>
                  </div>
                </div>
                <div className="requirement">
                  <span className="req-icon">📡</span>
                  <div className="req-content">
                    <span className="req-title">Range Proximity</span>
                    <span className="req-desc">Targets within sniffer radius</span>
                  </div>
                </div>
                <div className="requirement">
                  <span className="req-icon">🏠</span>
                  <div className="req-content">
                    <span className="req-title">Vulnerability</span>
                    <span className="req-desc">Not protected by safe zones</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="standby-panel">
              <button
                className="standby-button"
                disabled={true}
              >
                <span className="standby-icon">⏸️</span>
                <span>SCANNER ON STANDBY</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Discovered IP Addresses */}
      {discoveredIPs.length > 0 && (
        <div className="scanner-section">
          <div className="section-header">
            <h4>Discovered IP Addresses</h4>
            <button 
              className="clear-history-button"
              onClick={clearDiscoveredIPs}
              disabled={loading}
              title="Clear all discovered IP addresses"
            >
              🗑️ Clear IPs
            </button>
          </div>
          <div className="ip-list">
            {discoveredIPs.map((ip, index) => (
              <div key={index} className="ip-item">
                <div className="ip-info">
                  <span className="ip-address">{ip.address}</span>
                  <span className="ip-details">{ip.hostname} • {ip.os}</span>
                </div>
                <button
                  className={`btn-select ${selectedIP === ip.address ? 'selected' : ''}`}
                  onClick={() => setSelectedIP(ip.address)}
                >
                  {selectedIP === ip.address ? '✓ Selected' : 'Select'}
                </button>
              </div>
            ))}
          </div>
          
          <div className="target-scan-section">
            <h5>Target Analysis</h5>
            <div className="ip-input-section">
              <label htmlFor="target-ip-input">Enter Target IP Address:</label>
              <input
                id="target-ip-input"
                type="text"
                value={selectedIP}
                onChange={(e) => setSelectedIP(e.target.value)}
                placeholder="192.168.x.x or select from discovered IPs above"
                className="ip-input-field"
              />
              <button
                className="btn-primary"
                onClick={startTargetScan}
                disabled={!selectedIP || activeScan || loading}
              >
                🎯 Analyze Target ({calculateScanDuration()}h)
              </button>
            </div>
            {selectedIP && (
              <p className="target-info">Target: <strong>{selectedIP}</strong></p>
            )}
          </div>
        </div>
      )}

      {/* Bank Account Data */}
      {bankAccountData && (
        <div className="scanner-section bank-data">
          <h4>💰 Bank Account Data Extracted</h4>
          <div className="bank-account-info">
            <div className="account-header">
              <h5>{bankAccountData.username}'s Savings Account</h5>
              <span className="account-number">Account: {bankAccountData.accountNumber}</span>
            </div>
            
            <div className="account-details">
              <div className="balance-display">
                <span className="balance-label">Current Balance:</span>
                <span className="balance-amount">{formatCurrency(bankAccountData.balance)} WR Coins</span>
              </div>
              
              <div className="account-info-grid">
                <div className="info-item">
                  <span className="label">Bank:</span>
                  <span className="value">{bankAccountData.bankName}</span>
                </div>
                <div className="info-item">
                  <span className="label">Account Type:</span>
                  <span className="value">{bankAccountData.accountType}</span>
                </div>
                <div className="info-item">
                  <span className="label">Interest Rate (APY):</span>
                  <span className="value highlight-green">{bankAccountData.interestRate || 5}%</span>
                </div>
                <div className="info-item">
                  <span className="label">Total Interest Earned:</span>
                  <span className="value highlight-green">{formatCurrency(bankAccountData.totalInterestEarned || 0)} WR</span>
                </div>
                <div className="info-item">
                  <span className="label">Last Interest Calculation:</span>
                  <span className="value">{bankAccountData.lastInterestCalculation ? new Date(bankAccountData.lastInterestCalculation).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Account Status:</span>
                  <span className="value">{bankAccountData.isActive ? '🟢 Active' : '🔴 Inactive'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Last Activity:</span>
                  <span className="value">{bankAccountData.lastActivity}</span>
                </div>
                <div className="info-item">
                  <span className="label">Security Level:</span>
                  <span className="value">{bankAccountData.securityLevel}</span>
                </div>
              </div>
            </div>

            <div className="attack-section">
              <p className="attack-warning">⚠️ Attack Results: You get 56%, Occult Bank gets 14%, victim keeps 30%</p>
              <p className="swat-warning">🚔 WARNING: 13-hour attack with 70% SWAT detection risk!</p>
              <button
                className="btn-attack"
                onClick={bruteForceBankAccount}
                disabled={loading || activeScan}
              >
                💥 Attack Savings Account (13h)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scanner Radar Display - Always Show */}
      <div className="scanner-section">
        <h4>{activeScan && activeScan.status === 'running' ? 'Active Scan Radar' : 'Scanner Radar'}</h4>
        
        <div className="radar-container">
          <div className={`radar-screen ${
            nearbyUsers.length === 1 ? 'targets-1' :
            nearbyUsers.length === 2 ? 'targets-2' :
            nearbyUsers.length === 3 ? 'targets-3' :
            nearbyUsers.length === 4 ? 'targets-4' :
            nearbyUsers.length >= 5 ? 'targets-5-plus' : ''
          }`}>
            <div 
              className="radar-sweep"
              style={{
                animation: 'radarSweep 3s linear infinite',
                WebkitAnimation: 'radarSweep 3s linear infinite',
                animationPlayState: 'running',
                WebkitAnimationPlayState: 'running'
              }}
            ></div>
            <div className="radar-center"></div>
            
            {/* Radar rings */}
            <div className="radar-ring ring-1"></div>
            <div className="radar-ring ring-2"></div>
            <div className="radar-ring ring-3"></div>
            
            {/* Grid lines */}
            <div className="radar-grid-h"></div>
            <div className="radar-grid-v"></div>
            
            {/* User dots on radar - positioned by real relative locations */}
            {nearbyUsers.map((user, index) => {
              // Calculate real bearing/angle from user's computer to target user
              // This requires user coordinates - for now use a more realistic distribution
              const realAngle = user.bearing || ((index * 72 + (user.distance * 3.7)) % 360); // More realistic spread
              const distanceScale = Math.min(user.distance / 100, 0.8); // Scale to radar (max 80% of radius)
              const radarDistance = 20 + (distanceScale * 110); // 20-130px from center
              const x = 50 + radarDistance * Math.cos((realAngle - 90) * Math.PI / 180);
              const y = 50 + radarDistance * Math.sin((realAngle - 90) * Math.PI / 180);
              
              return (
                <div
                  key={index}
                  className="radar-target"
                  style={{
                    left: `${Math.max(5, Math.min(95, x))}%`,
                    top: `${Math.max(5, Math.min(95, y))}%`
                  }}
                  title={`${user.username} (${user.distance}m)`}
                >
                  <div 
                    className="target-pulse"
                    style={{
                      animation: 'targetPulse 2s ease-in-out infinite',
                      WebkitAnimation: 'targetPulse 2s ease-in-out infinite',
                      animationPlayState: 'running',
                      WebkitAnimationPlayState: 'running'
                    }}
                  ></div>
                  <div className="target-dot"></div>
                </div>
              );
            })}
          </div>
          
          <div className="radar-info">
            <div className="scan-status-display">
              <div className="status-text">
                {activeScan && activeScan.status === 'running' ? (
                  <>🔍 {activeScan.scanType === 'network' ? 'SCANNING NETWORK' : 
                         activeScan.scanType === 'target' ? 'ANALYZING TARGET' : 
                         'BRUTE FORCE ATTACK'}</>
                ) : (
                  <>📡 RADAR STANDBY</>
                )}
              </div>
              <div className="targets-count">
                {nearbyUsers.length} TARGET{nearbyUsers.length !== 1 ? 'S' : ''} DETECTED
                {nearbyUsers.length > 0 && (
                  <span className="radar-status-indicator">
                    {nearbyUsers.length === 1 ? ' • SINGLE CONTACT' :
                     nearbyUsers.length === 2 ? ' • DUAL CONTACTS' :
                     nearbyUsers.length === 3 ? ' • MULTIPLE CONTACTS' :
                     nearbyUsers.length === 4 ? ' • HIGH ACTIVITY' :
                     nearbyUsers.length >= 5 ? ' • MAXIMUM DENSITY' : ''} • REAL POSITION
                  </span>
                )}
              </div>
              <div className="radar-frequency">
                SCAN FREQUENCY: {nearbyUsers.length > 0 ? 
                  `${(3000 / Math.max(nearbyUsers.length, 1)).toFixed(0)}Hz` : 
                  '1000Hz'
                }
              </div>
            </div>
          </div>
          
          {/* Detected Users List */}
          {nearbyUsers.length > 0 && (
            <div className="detected-users-list">
              <h5>📍 Detected Users ({nearbyUsers.length})</h5>
              <div className="users-grid">
                {nearbyUsers.map((user, index) => (
                  <div key={index} className="user-card">
                    <div className="user-header">
                      <span className="user-icon">👤</span>
                      <span className="username">{user.username}</span>
                      <span className="distance">{user.distance}m</span>
                    </div>
                    <div className="user-status">
                      <span className="status-dot"></span>
                      <span className="status-text">Computer Active</span>
                    </div>
                    <div className="radar-position">
                      Radar Position: {(() => {
                        const realAngle = user.bearing || ((index * 72 + (user.distance * 3.7)) % 360);
                        const direction = 
                          realAngle >= 0 && realAngle < 22.5 ? 'N' :
                          realAngle >= 22.5 && realAngle < 67.5 ? 'NE' :
                          realAngle >= 67.5 && realAngle < 112.5 ? 'E' :
                          realAngle >= 112.5 && realAngle < 157.5 ? 'SE' :
                          realAngle >= 157.5 && realAngle < 202.5 ? 'S' :
                          realAngle >= 202.5 && realAngle < 247.5 ? 'SW' :
                          realAngle >= 247.5 && realAngle < 292.5 ? 'W' : 'NW';
                        return `${direction} • ${Math.round(realAngle)}°`;
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scan History */}
      <div className="scanner-section">
        <div className="section-header">
          <h4>Recent Activity</h4>
          {scanHistory.length > 0 && (
            <button 
              className="clear-history-button"
              onClick={clearScanHistory}
              disabled={loading}
              title="Clear all scan history"
            >
              🗑️ Clear History
            </button>
          )}
        </div>
        <div className="scan-history">
          {scanHistory.length > 0 ? (
            <div className="history-list">
              {scanHistory.slice(0, 5).map((scan, index) => (
                <div key={index} className="history-item">
                  <div className="scan-info">
                    <div className="scan-target">{scan.target || scan.scanType}</div>
                    <div className="scan-meta">
                      <span className="scan-type">{scan.scanType.toUpperCase()}</span>
                      <span className="scan-time">{formatScanTime(scan.completedAt)}</span>
                      <span className={`scan-status ${scan.status}`}>{scan.status}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-history">
              <p>No scan history available. Start your first network scan to begin.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Scanner;